import { Box, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { formatCampaignGoalShort } from '@/utils/blackNovemberCampaign';
import { BLACK_CAKTO, BLACK_CAKTO_ASSETS } from './blackNovemberTheme';

/** Os marcos da régua do site: um quarto, metade e três quartos da meta. */
const MILESTONES = [0.25, 0.5, 0.75];

/**
 * A régua até o troféu, a mesma do card de exemplo do site: trilho fino, marcos
 * em 25/50/75% da meta e o troféu em traço no fim. A faixa usa a versão sem
 * rótulo; o dialog mostra o valor de cada marco.
 */
export default function BlackNovemberMilestones({
  percent,
  goal,
  label,
  muted = false,
  showLabels = false,
  sx,
}) {
  const reached = Math.min(100, Math.max(0, percent || 0));
  const marks = MILESTONES.map((share) => ({
    share,
    done: reached >= share * 100,
  }));

  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={reached}
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            height: 4,
            borderRadius: 99,
            background: BLACK_CAKTO.track,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: '0 auto 0 0',
              width: `${reached}%`,
              minWidth: reached > 0 ? 4 : 0,
              borderRadius: 99,
              background: muted ? BLACK_CAKTO.barMuted : BLACK_CAKTO.bar,
              transition: 'width .6s cubic-bezier(.22,.7,.3,1)',
            }}
          />
          {marks.map(({ share, done }) => (
            <Box
              key={share}
              sx={{
                position: 'absolute',
                top: '50%',
                left: `${share * 100}%`,
                width: 7,
                height: 7,
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                border: '1px solid',
                borderColor: done && !muted ? BLACK_CAKTO.mintLine : BLACK_CAKTO.lineStrong,
                background: done && !muted ? BLACK_CAKTO.mintLine : '#0A100C',
              }}
            />
          ))}
        </Box>

        <Box
          component="img"
          src={BLACK_CAKTO_ASSETS.trophyOutline}
          alt=""
          aria-hidden
          sx={{
            width: 16,
            height: 16,
            flexShrink: 0,
            opacity: muted ? 0.45 : 0.9,
          }}
        />
      </Stack>

      {showLabels ? (
        <Box sx={{ position: 'relative', height: 16, mt: 0.75, mr: 3 }}>
          {[0, ...MILESTONES, 1].map((share) => (
            <Typography
              key={share}
              component="span"
              sx={{
                position: 'absolute',
                left: `${share * 100}%`,
                transform: share === 0 ? 'none' : 'translateX(-50%)',
                fontSize: 10.5,
                color: BLACK_CAKTO.muted,
                whiteSpace: 'nowrap',
                fontFamily: BLACK_CAKTO.font,
                ...(share === 1 ? { transform: 'none', left: 'auto', right: -24 } : {}),
              }}
            >
              {formatCampaignGoalShort(goal * share)}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

BlackNovemberMilestones.propTypes = {
  percent: PropTypes.number,
  goal: PropTypes.number,
  label: PropTypes.string,
  muted: PropTypes.bool,
  showLabels: PropTypes.bool,
  sx: PropTypes.object,
};
